<template>
  <div class="min-h-screen pb-20">
    <!-- Header -->
    <header class="bg-white shadow-sm sticky top-0 z-10">
      <div class="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <button
            @click="$router.push('/lists')"
            class="text-gray-600 hover:text-gray-900"
          >
            ← Back
          </button>
          <h1 class="text-2xl font-bold text-gray-900">Invitations</h1>
        </div>
        <UserMenu />
      </div>
    </header>

    <!-- Content -->
    <main class="max-w-7xl mx-auto px-4 py-6">
      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
      </div>

      <!-- Empty State -->
      <div v-else-if="invitations.length === 0" class="text-center py-12">
        <div class="text-6xl mb-4">📬</div>
        <h2 class="text-xl font-semibold text-gray-900 mb-2">No pending invitations</h2>
        <p class="text-gray-600">You're all caught up!</p>
      </div>

      <!-- Invitations List -->
      <div v-else class="space-y-4">
        <div
          v-for="invitation in invitations"
          :key="invitation.id"
          class="card"
        >
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1">
              <h3 class="text-lg font-semibold text-gray-900">
                {{ invitation.shopping_list_name }}
              </h3>
              <p class="text-sm text-gray-600 mt-1">
                Invited by <span class="font-medium">{{ invitation.inviter_email }}</span>
              </p>
              <div class="flex items-center gap-2 mt-2">
                <span class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {{ invitation.can_edit ? 'Can edit' : 'View only' }}
                </span>
                <span class="text-xs text-gray-500">
                  {{ formatDate(invitation.created_at) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 mt-4">
            <button
              @click="handleDecline(invitation)"
              class="btn btn-secondary flex-1"
              :disabled="processing === invitation.id"
            >
              Decline
            </button>
            <button
              @click="handleAccept(invitation)"
              class="btn btn-primary flex-1"
              :disabled="processing === invitation.id"
            >
              {{ processing === invitation.id ? 'Accepting...' : 'Accept' }}
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import apiClient from '../api/client'
import UserMenu from '../components/UserMenu.vue'

const router = useRouter()
const invitations = ref([])
const loading = ref(true)
const processing = ref(null)

const fetchInvitations = async () => {
  try {
    const response = await apiClient.get('/api/lists/invitations/pending')
    invitations.value = response.data
  } catch (error) {
    console.error('Failed to fetch invitations:', error)
  } finally {
    loading.value = false
  }
}

const handleAccept = async (invitation) => {
  processing.value = invitation.id
  try {
    await apiClient.post(`/api/lists/invitations/${invitation.id}/accept`)
    invitations.value = invitations.value.filter((i) => i.id !== invitation.id)

    // Navigate to the list
    router.push(`/lists/${invitation.shopping_list_id}`)
  } catch (error) {
    console.error('Failed to accept invitation:', error)
    alert('Failed to accept invitation')
  } finally {
    processing.value = null
  }
}

const handleDecline = async (invitation) => {
  processing.value = invitation.id
  try {
    await apiClient.post(`/api/lists/invitations/${invitation.id}/decline`)
    invitations.value = invitations.value.filter((i) => i.id !== invitation.id)
  } catch (error) {
    console.error('Failed to decline invitation:', error)
    alert('Failed to decline invitation')
  } finally {
    processing.value = null
  }
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString()
}

onMounted(() => {
  fetchInvitations()
})
</script>
